import { ghostBtnClass } from "@/components/manufacturing/manufacturingSurfaceClasses";
import {
  DEFAULT_SHELF_ALERT_PCT,
  shelfCostIsIncomplete,
  shelfVariance,
  shelfVarianceAttention,
  shelfVarianceAttentionTitle,
} from "@/lib/manufacturing/cogsShelfVariance";
import { shelfVarianceSteps } from "@/lib/manufacturing/cogsShelfVarianceReport";
import type { MfgPricingLayerColumn, MfgPricingLayerGridRow } from "@/lib/manufacturing/types";
import { SHELF_ATTENTION_TONE, SHELF_VARIANCE_TONE } from "./PricingBandTotalCell";
import { PricingUnattachedColumnVariance } from "./PricingUnattachedColumnVariance";
import { formatUsd } from "./pricingGridFormat";

const BAND_LABEL: Record<string, string> = {
  start: "Start",
  cogs: "COGS",
  wholesale: "Wholesale",
  msrp: "MSRP",
};

function CompareCard({
  title,
  calculated,
  shelf,
  incomplete,
  alertThresholdPct,
}: {
  title: string;
  calculated: number;
  shelf: number | null | undefined;
  incomplete: boolean;
  alertThresholdPct: number;
}) {
  const variance = shelfVariance(calculated, shelf);
  const attention = shelfVarianceAttention(variance, incomplete, alertThresholdPct);
  const tone = attention === "quiet" ? SHELF_VARIANCE_TONE[variance.tone] : SHELF_ATTENTION_TONE[attention];
  return (
    <div className="rounded-lg border border-gray-200 px-3 py-2 dark:border-zinc-700">
      <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-zinc-400">{title}</div>
      <div className="mt-1 text-sm">Shelf {shelf == null ? "—" : formatUsd(shelf)}</div>
      <div className="text-sm">Calculated {formatUsd(calculated)}</div>
      <div className={`text-sm ${tone}`} title={shelfVarianceAttentionTitle(attention, variance, alertThresholdPct)}>
        {variance.label}
        {attention === "alert" ? " · Alert" : attention === "warning" ? " · Warning" : ""}
      </div>
    </div>
  );
}

export function PricingShelfVarianceDrill({
  row,
  columns,
  onClose,
  alertThresholdPct = DEFAULT_SHELF_ALERT_PCT,
}: {
  row: MfgPricingLayerGridRow;
  columns: readonly MfgPricingLayerColumn[];
  onClose: () => void;
  alertThresholdPct?: number;
}) {
  const steps = shelfVarianceSteps(row, columns);
  const incomplete = shelfCostIsIncomplete(row);
  return (
    <div className="mb-3 rounded-xl border border-gray-200 bg-gray-50 p-3 dark:border-zinc-700 dark:bg-zinc-900">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <div className="font-semibold text-gray-900 dark:text-zinc-100">{row.name}</div>
          <div className="text-xs text-gray-500 dark:text-zinc-400">
            {[row.code, row.size_band, row.series, row.roof_style].filter(Boolean).join(" · ")}
          </div>
        </div>
        <button type="button" className={ghostBtnClass} onClick={onClose}>
          Close
        </button>
      </div>
      <div className="mt-3 grid gap-3 sm:grid-cols-2">
        <CompareCard title="Wholesale" calculated={row.wholesale_total_usd} shelf={row.shelf_wholesale_usd} incomplete={incomplete} alertThresholdPct={alertThresholdPct} />
        <CompareCard title="MSRP" calculated={row.msrp_total_usd} shelf={row.shelf_msrp_usd} incomplete={incomplete} alertThresholdPct={alertThresholdPct} />
      </div>
      <table className="mt-3 w-full text-left text-xs">
        <thead>
          <tr className="text-[11px] uppercase tracking-wide text-gray-500 dark:text-zinc-400">
            <th className="py-1 pr-2 font-semibold">Step</th>
            <th className="py-1 pr-2 font-semibold">Band</th>
            <th className="py-1 pr-2 text-right font-semibold">Amount</th>
            <th className="py-1 text-right font-semibold">Running</th>
          </tr>
        </thead>
        <tbody>
          {steps.map((step, index) => (
            <tr key={`${step.band}-${step.label}-${index}`} className="border-t border-gray-200 dark:border-zinc-800">
              <td className="py-1 pr-2">{step.label}</td>
              <td className="py-1 pr-2 text-gray-500 dark:text-zinc-400">{BAND_LABEL[step.band]}</td>
              <td className="py-1 pr-2 text-right tabular-nums">{formatUsd(step.amount_usd)}</td>
              <td className="py-1 text-right tabular-nums">{formatUsd(step.running_after)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <PricingUnattachedColumnVariance row={row} columns={columns} />
    </div>
  );
}
